import React, { useState } from "react";
import { useAuth } from "../../software/AuthContext";
import { Waves, Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [dayClosed, setDayClosed] = useState(() => sessionStorage.getItem("anx_day_closed"));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await login(email, password); sessionStorage.removeItem("anx_day_closed"); }
    catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  if (dayClosed) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-[#0B1120] text-white p-6 text-center" data-testid="day-closed-screen">
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Day closed</h1>
      <p className="text-slate-300 max-w-md">The end of day is saved and the database is closed. You can shut the computer down now.</p>
      <button onClick={() => { sessionStorage.removeItem("anx_day_closed"); setDayClosed(null); }} data-testid="day-closed-signin"
        className="rounded-full bg-sky-600 hover:bg-sky-500 px-6 py-2.5 font-semibold transition-colors">Sign in again</button>
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B1120]" data-testid="software-login">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-sky-900 to-slate-900 text-white">
        <div className="flex items-center gap-2 font-display font-bold text-lg"><Waves className="h-5 w-5 text-sky-400" /> Aqualux Nexus</div>
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-tight">Pool Software</h1>
          <p className="mt-3 text-slate-300 max-w-sm">One client database for the shop, the mobile technician and the home automation unit — every water test in one history.</p>
        </div>
        <p className="text-xs text-slate-500 font-mono">White-label · Store Admin · Technician · Client</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl p-8 shadow-2xl">
          <h2 className="font-display text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Access your pool software dashboard.</p>
          <label className="block mt-6 text-sm font-medium text-slate-700">Email</label>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="mt-1 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" placeholder="you@shop.com" />
          <label className="block mt-4 text-sm font-medium text-slate-700">Password</label>
          <div className="relative mt-1">
            <input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 pr-11 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" placeholder="••••••••" />
            <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="login-password-toggle"
              aria-label={showPw ? "Hide password" : "Show password"} title={showPw ? "Hide password" : "Show password"}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 transition-colors">
              {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {err && <p className="mt-3 text-sm text-red-600" data-testid="login-error">{err}</p>}
          <button data-testid="login-submit" disabled={busy} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white font-semibold py-3 transition-colors disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
